/**
 * PROJECT — the learner's own, carried across a ladder.
 *
 * FOUNDER, 2026-08-15: "all ai courses were supposed to have a user own project along with
 * evaluating ankr products" → "build p1".
 *
 * ── THE STANDARD IS ON SCREEN FROM THE FIRST DAY ──────────────────────────────────────────────
 * The format spec's law: a rubric revealed after submission is an examiner's opinion; a rubric
 * published in advance is a specification. So this page shows every decision that will be asked
 * for, in the order the learner meets them, before they have made a single one. There is nothing
 * to discover at the end.
 *
 * ── NO SCORE, NO PERCENTAGE, NO BAR ───────────────────────────────────────────────────────────
 * The capstone asks whether every decision has been made. "Nine of twelve" is a fact and a useful
 * one; "75% complete" implies a design can be three-quarters right, which is not a thing that can
 * be checked. The open decisions are named instead — a check that cannot say what is missing is a
 * grade wearing a check's clothes.
 *
 * ── IT IS THEIRS ──────────────────────────────────────────────────────────────────────────────
 * Nothing here shows anyone else's project, and there is no browse, no gallery and no aggregate.
 * The only thing this surface will say about a project to anyone but its owner is whether a check
 * passed — the least that a certificate can rest on.
 */

import { useState } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused, absent } from '../../../packages/ankr-provenance/src/provenance.mjs';
import MANIFEST from '../../project-manifest.json';
import {
  carriesProject, ladderFor, chainFor, progressOf, checkArtefact,
} from './projectspine.mjs';

const STORE = 'ankr-academy-project';
const load = () => { try { return JSON.parse(localStorage.getItem(STORE) || '{}'); } catch { return {}; } };
const save = (p) => { try { localStorage.setItem(STORE, JSON.stringify(p)); } catch { /* a full or blocked store is not worth breaking the page over */ } };

export default function ProjectLab({ level = 'CODEAI101' }) {
  const [proj, setProj] = useState(() => load());
  const [open, setOpen] = useState(null);
  const [draft, setDraft] = useState('');

  if (!carriesProject(MANIFEST, level)) {
    const row = ladderFor(MANIFEST, level);
    return (
      <div className="g-glass shell-panel lab">
        <div className="g-label">Project</div>
        <h2 className="g-h2 lab-h">This ladder carries no project</h2>
        <Value of={absent()} />
        <p className="g-body">{row?.not_applicable_because ?? 'Not declared in the project manifest.'}</p>
        <p className="g-body g-dim">
          Declared, rather than silently omitted. A ladder that carries no project says why.
        </p>
      </div>
    );
  }

  const c = checkArtefact(MANIFEST, level, proj);
  const p = progressOf(MANIFEST, level, proj);
  const row = ladderFor(MANIFEST, level);
  const named = typeof proj.name === 'string' && proj.name.trim().length > 0;

  const setName = (v) => { const next = { ...proj, name: v }; setProj(next); save(next); };
  const fill = (id, value) => {
    const next = { ...proj, slots: { ...(proj.slots || {}), [id]: { value, filled_on: new Date().toISOString().slice(0, 10) } } };
    setProj(next); save(next); setOpen(null); setDraft('');
  };
  const clear = (id) => {
    const slots = { ...(proj.slots || {}) }; delete slots[id];
    const next = { ...proj, slots }; setProj(next); save(next);
  };

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Project · yours, carried across {p.chain.join(' → ')}</div>
      <h2 className="g-h2 lab-h">{row.artefact}</h2>

      {/* ── NAME IT ── */}
      <div className="lab-task">
        <div className="g-label">What are you building?</div>
        <p className="g-body g-dim">
          One project, chosen once, carried through every chapter. Not an exercise — the thing you
          would actually want to exist.
        </p>
        <div className="lab-task-head">
          <input className="morse-input prj-name" value={proj.name || ''}
            placeholder="e.g. a tool that tracks my shop's stock"
            onChange={(e) => setName(e.target.value)} />
        </div>
        {!named && <p className="g-body g-dim">Name it and the decisions below become yours to make.</p>}
      </div>

      {/* ── THE PUBLISHED STANDARD ── */}
      <div className="lab-task">
        <div className="g-label">
          What this ladder will ask you to decide — all {c.published_standard.length}, published now
        </div>
        <p className="g-body g-dim">
          Every one of these is asked by a chapter, in the founder's own words. Nothing is revealed
          at the end; the check is the list.
        </p>
        <div className="prj-slots">
          {p.all.map((s) => {
            const filled = proj.slots?.[s.id];
            const isOpen = open === s.id;
            return (
              <div key={s.id} className={`prj-slot ${filled ? 'prj-done' : ''}`}>
                <div className="prj-head">
                  <span className="prj-tick">{filled ? '✓' : '○'}</span>
                  <b>{s.asks}</b>
                  <span className="cou-sp" />
                  <span className="prj-where">{s.level} · ch{s.chapter}</span>
                </div>
                {filled && !isOpen && (
                  <p className="g-body prj-value">{filled.value}</p>
                )}
                {isOpen ? (
                  <div className="prj-edit">
                    <textarea className="prj-ta" rows={3} value={draft} autoFocus
                      placeholder={s.asks}
                      onChange={(e) => setDraft(e.target.value)} />
                    <div className="lab-task-head">
                      <button className="g-btn g-btn-primary" disabled={!draft.trim()}
                        onClick={() => fill(s.id, draft.trim())}>Save</button>
                      <button className="g-btn" onClick={() => { setOpen(null); setDraft(''); }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="lab-task-head">
                    <button className="g-btn" disabled={!named}
                      onClick={() => { setOpen(s.id); setDraft(filled?.value || ''); }}>
                      {filled ? 'Change' : 'Decide this'}
                    </button>
                    {filled && <button className="g-btn" onClick={() => clear(s.id)}>Clear</button>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── THE CHECK, WHICH IS THE SAME BEFORE AND AFTER ── */}
      <div className="lab-task">
        <div className="g-label">The check — unchanged since before you started</div>
        <div className={`prj-verdict ${c.passes ? 'prj-pass' : ''}`}>
          {c.passes
            ? <Value of={computed(c.verdict)} />
            : <Value of={refused(c.verdict)} />}
        </div>
        {!c.passes && (
          <>
            <p className="g-body">Still open, by name:</p>
            <ul className="fai-list">
              {c.missing.map((m) => <li key={m.id}>{m.asks} <span className="prj-where">({m.level} ch{m.chapter})</span></li>)}
            </ul>
          </>
        )}
        {c.passes && (
          <p className="g-body prj-done-note">
            Every decision this ladder asks for has been made. That is what the check tests — not
            whether the answers are good, which is yours and your reviewer's to judge.
          </p>
        )}
      </div>

      <p className="g-body g-dim lab-why">
        This is not a new requirement. Code101 and CodeAI101 have asked for a personal project across
        fourteen days and twenty-eight paired conversations since they were written; the capstone
        enumerates these decisions by name. What was missing was somewhere for them to live, so that
        each conversation stops asking you to paste your project in again.
        <br />
        <Value of={cited('the twelve carried decisions', 'CodeAI101 module 07 §7 — the capstone paired conversation')} />
        <br />
        No score and no percentage: the capstone asks whether every decision is made, and a design
        cannot be three-quarters right. And this stays on your machine — nothing here shows anyone
        else's project, and the only thing said about yours to anyone else is whether the check
        passed.
      </p>
    </div>
  );
}
